import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import {
  X,
  ChevronRight,
  ChevronLeft,
  Sparkles,
  LayoutDashboard,
  ClipboardList,
  Wrench,
  FolderOpen,
  CreditCard,
  Video,
  User,
} from "lucide-react";

const TOUR_STEPS = [
  {
    target: "[data-tour='dashboard']",
    title: "Panel Principal",
    description: "Aquí verás un resumen de tu progreso, próximas tareas y sesiones programadas.",
    position: "right",
    icon: LayoutDashboard,
  },
  {
    target: "[data-tour='activities']",
    title: "Mis Actividades",
    description: "Tu centro de operaciones. Completa las actividades de cada bloque para avanzar en el programa.",
    position: "right",
    icon: ClipboardList,
  },
  {
    target: "[data-tour='tools']",
    title: "Herramientas",
    description: "Simulador de rentabilidad, control de leads y métricas para gestionar tu centro.",
    position: "right",
    icon: Wrench,
  },
  {
    target: "[data-tour='documents']",
    title: "Documentos",
    description: "Accede a tu contrato firmado y facturas del programa.",
    position: "right",
    icon: FolderOpen,
  },
  {
    target: "[data-tour='payments']",
    title: "Pagos",
    description: "Consulta el estado de tus pagos y cuotas pendientes.",
    position: "right",
    icon: CreditCard,
  },
  {
    target: "[data-tour='sessions']",
    title: "Sesiones",
    description: "Revisa tus sesiones de consultoría individuales.",
    position: "right",
    icon: Video,
  },
  {
    target: "[data-tour='profile']",
    title: "Tu Perfil",
    description: "Gestiona tus datos y cambia tu contraseña cuando quieras.",
    position: "top",
    icon: User,
  },
];

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return isMobile;
}

function MobileStepCard({ step, currentStep, totalSteps, onNext, onPrev, onClose }) {
  const isFirst = currentStep === 0;
  const isLast = currentStep === totalSteps - 1;
  const Icon = step.icon;

  return (
    <div className="fixed inset-0 z-[100000] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60" />
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6 animate-fade-in">
        <button
          onClick={onClose}
          className="absolute top-3 right-3 p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
        >
          <X size={18} />
        </button>

        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-xl bg-brand-light flex items-center justify-center">
            <Icon size={24} className="text-brand" />
          </div>
          <div>
            <p className="text-xs text-gray-400 font-medium">Paso {currentStep + 1} de {totalSteps}</p>
            <h4 className="font-bold text-gray-900">{step.title}</h4>
          </div>
        </div>

        <p className="text-sm text-gray-600 leading-relaxed mb-6">
          {step.description}
        </p>

        <div className="flex gap-2">
          {!isFirst && (
            <button
              onClick={onPrev}
              className="flex-1 py-2.5 text-sm text-gray-600 border border-gray-200 rounded-xl hover:bg-gray-50 flex items-center justify-center gap-1"
            >
              <ChevronLeft size={16} />
              Atrás
            </button>
          )}
          <button
            onClick={isLast ? onClose : onNext}
            className="flex-1 py-2.5 bg-brand text-white text-sm font-semibold rounded-xl hover:bg-brand-dark flex items-center justify-center gap-1"
          >
            {isLast ? "¡Empezar!" : "Siguiente"}
            {!isLast && <ChevronRight size={16} />}
          </button>
        </div>
      </div>
    </div>
  );
}

function DesktopTooltip({ step, currentStep, totalSteps, onNext, onPrev, onClose, targetRect }) {
  if (!targetRect) return null;

  const padding = 12;
  const tooltipWidth = 300;

  let style = {};
  
  if (step.position === "right") {
    style = {
      top: targetRect.top + targetRect.height / 2,
      left: targetRect.right + padding,
      transform: "translateY(-50%)",
    };
  } else if (step.position === "top") {
    style = {
      bottom: window.innerHeight - targetRect.top + padding,
      left: targetRect.left + targetRect.width / 2,
      transform: "translateX(-50%)",
    };
  } else if (step.position === "bottom") {
    style = {
      top: targetRect.bottom + padding,
      left: targetRect.left + targetRect.width / 2,
      transform: "translateX(-50%)",
    };
  }

  const isFirst = currentStep === 0;
  const isLast = currentStep === totalSteps - 1;

  return (
    <div
      className="fixed z-[100001] bg-white rounded-xl shadow-2xl border border-gray-200 p-4 animate-fade-in"
      style={{ ...style, width: tooltipWidth }}
    >
      <button
        onClick={onClose}
        className="absolute top-2 right-2 p-1 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
      >
        <X size={16} />
      </button>

      <div className="flex items-center gap-2 mb-2">
        <div className="w-6 h-6 rounded-full bg-brand-light flex items-center justify-center">
          <span className="text-xs font-bold text-brand">{currentStep + 1}</span>
        </div>
        <h4 className="font-semibold text-gray-900 text-sm">{step.title}</h4>
      </div>

      <p className="text-sm text-gray-600 leading-relaxed mb-4">
        {step.description}
      </p>

      <div className="flex items-center justify-between">
        <span className="text-xs text-gray-400">
          {currentStep + 1} de {totalSteps}
        </span>
        <div className="flex gap-2">
          {!isFirst && (
            <button
              onClick={onPrev}
              className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 flex items-center gap-1"
            >
              <ChevronLeft size={14} />
              Atrás
            </button>
          )}
          <button
            onClick={isLast ? onClose : onNext}
            className="px-4 py-1.5 bg-brand text-white text-sm font-medium rounded-lg hover:bg-brand-dark flex items-center gap-1"
          >
            {isLast ? "¡Empezar!" : "Siguiente"}
            {!isLast && <ChevronRight size={14} />}
          </button>
        </div>
      </div>
    </div>
  );
}

function Spotlight({ targetRect }) {
  if (!targetRect) return null;

  const padding = 4;

  return (
    <div
      className="fixed z-[99999] rounded-lg ring-4 ring-brand/50 pointer-events-none transition-all duration-300"
      style={{
        top: targetRect.top - padding,
        left: targetRect.left - padding,
        width: targetRect.width + padding * 2,
        height: targetRect.height + padding * 2,
        boxShadow: "0 0 0 9999px rgba(0, 0, 0, 0.5)",
      }}
    />
  );
}

function WelcomeModal({ onStart, onSkip }) {
  return (
    <div className="fixed inset-0 z-[100000] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60" onClick={onSkip} />
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 text-center animate-fade-in">
        <div className="w-16 h-16 rounded-2xl bg-brand-light flex items-center justify-center mx-auto mb-4">
          <Sparkles size={32} className="text-brand" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          ¡Bienvenido/a al Portal!
        </h2>
        <p className="text-gray-600 mb-6">
          Te damos un tour rápido de 30 segundos para que conozcas todas las funciones del portal.
        </p>
        <div className="flex flex-col gap-3">
          <button
            onClick={onStart}
            className="w-full py-3 bg-brand text-white font-semibold rounded-xl hover:bg-brand-dark transition-colors"
          >
            Comenzar Tour
          </button>
          <button
            onClick={onSkip}
            className="w-full py-2 text-gray-500 hover:text-gray-700 text-sm"
          >
            Saltar por ahora
          </button>
        </div>
      </div>
    </div>
  );
}

export default function GuidedTour({ onComplete }) {
  const [phase, setPhase] = useState("welcome"); // welcome | touring | done
  const [currentStep, setCurrentStep] = useState(0);
  const [targetRect, setTargetRect] = useState(null);
  const isMobile = useIsMobile();

  useEffect(() => {
    if (phase !== "touring" || isMobile) return;

    const step = TOUR_STEPS[currentStep];
    const target = document.querySelector(step.target);

    if (target) {
      const rect = target.getBoundingClientRect();
      setTargetRect(rect);
      target.scrollIntoView({ behavior: "smooth", block: "center" });
    } else {
      setTargetRect(null);
    }
  }, [phase, currentStep, isMobile]);

  const handleStart = () => setPhase("touring");

  const handleNext = () => {
    if (currentStep < TOUR_STEPS.length - 1) {
      setCurrentStep((s) => s + 1);
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep((s) => s - 1);
    }
  };

  const handleClose = () => {
    setPhase("done");
    onComplete?.();
  };

  if (phase === "done") return null;

  const content = (
    <>
      {phase === "welcome" && (
        <WelcomeModal onStart={handleStart} onSkip={handleClose} />
      )}

      {phase === "touring" && (
        isMobile ? (
          <MobileStepCard
            step={TOUR_STEPS[currentStep]}
            currentStep={currentStep}
            totalSteps={TOUR_STEPS.length}
            onNext={handleNext}
            onPrev={handlePrev}
            onClose={handleClose}
          />
        ) : (
          <>
            <Spotlight targetRect={targetRect} />
            <DesktopTooltip
              step={TOUR_STEPS[currentStep]}
              currentStep={currentStep}
              totalSteps={TOUR_STEPS.length}
              onNext={handleNext}
              onPrev={handlePrev}
              onClose={handleClose}
              targetRect={targetRect}
            />
          </>
        )
      )}
    </>
  );

  return createPortal(content, document.body);
}
